import type { FC, PointerEvent, ReactNode } from 'react'

import { useRef } from 'react'
import { motion, useMotionValue, useReducedMotion, useSpring } from 'motion/react'
import { cn } from '@/lib/utils'

interface MagneticProps {
    children: ReactNode
    strength?: number
    className?: string
}

// Pulls its content toward the pointer while hovered, then springs back.
export const Magnetic: FC<MagneticProps> = ({ children, strength = 0.35, className }): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)
    const reduce = useReducedMotion()
    const x = useMotionValue(0)
    const y = useMotionValue(0)
    const springX = useSpring(x, { stiffness: 220, damping: 18, mass: 0.4 })
    const springY = useSpring(y, { stiffness: 220, damping: 18, mass: 0.4 })

    const onMove = (event: PointerEvent<HTMLDivElement>) => {
        if (reduce || event.pointerType !== 'mouse' || !ref.current) return

        const rect = ref.current.getBoundingClientRect()

        x.set((event.clientX - (rect.left + rect.width / 2)) * strength)
        y.set((event.clientY - (rect.top + rect.height / 2)) * strength)
    }

    const onLeave = () => {
        x.set(0)
        y.set(0)
    }

    return (
        <motion.div
            ref={ref}
            className={cn('inline-block', className)}
            style={{ x: springX, y: springY }}
            onPointerMove={onMove}
            onPointerLeave={onLeave}
        >
            {children}
        </motion.div>
    )
}
